import { VNode } from 'preact';
import { render } from 'preact-render-to-string';

export type ViewLayoutDefinition<
  Locals extends Record<string, unknown> = Record<string, unknown>,
  // Forbidden key: used to check if a VNode was passed to Extends.
  Outlets extends { content: string, type?: never } = { content: string, type?: never }
> = (
  locals: Locals,
  outlets: Outlets
) => string

export function Extends<
  Locals extends Record<string, unknown>,
  Outlets extends { content: string, type?: never }
>(
  layout: ViewLayoutDefinition<Locals, Outlets>,
  extension: (props: NoInfer<Locals>) => (VNode | string | { [k in keyof Outlets]: VNode | string })
): (props: Locals) => string {
  return (props) => {
    const locals: Locals = props;
    const result = extension(props);
    if (typeof result === 'string') {
      return layout(
        locals,
        {
          content: result
        } as Outlets);
    } else if (typeof result === 'object' && 'key' in result) {
      return layout(
        locals,
        {
          content: render(result as VNode)
        } as Outlets);
    } else {
      const renderedOutlets = Object.entries(result).reduce((acc, [k, v]) => {
        const rendered = typeof v === 'string' ? v : render(v);
        acc[k as keyof Outlets] = rendered as Outlets[keyof Outlets];
        return acc;
      }, {} as Outlets);

      return layout(
        locals,
        renderedOutlets
      );
    }
  };
}
